import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useMemo } from 'react'

import { IProductCard } from '@src/components/product-card/product-card.types'
import { LoaderWrapper } from '@src/quince-components/src'
import { NAV_POSITION } from '@src/quince-components/src/atoms/swiper/Swiper'
import MiniSimilarProducts from '@src/quince-components/src/molecules/miniSimilarProducts'
import { getSelectedColorFromSelectedOptions } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import ProductListSectionTwo from '@src/quince-components/src/molecules/productListSectionTwo'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useFetch from '@src/quince-site-common/src/hooks/useFetch'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { RECOMMENDED_SECTION_IDS } from '@src/quince-site-pdp/src/contexts/productPageContext.constants'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import { FINDING_METHOD_AGGREGATE } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { productContextInitialState } from '../../../contexts/ProductPageContext'
import ProductPageSectionProps from '../../../domains/ProductPageSectionProps'
import useProductPageContext from '../../../hooks/useProductContext'
import { useIsNewRecommendationsDesign } from '../../../templates/productPage/productPage.utils'
import {
  getSelectedColor,
  getRecommendedProductsApi,
  RECOMMENDED_PRODUCTS_TYPE,
  IGetRecommendedProductsApiParams,
} from '../utils'

import { SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF } from './constant'
import { getTrackingMetaData } from './SimilarProducts.constants'
import styles from './similarProducts.module.scss'

const SimilarProducts = (props: ProductPageSectionProps | BundlePageSectionProps) => {
  const { t } = useTranslation('common')
  const product = 'bundle' in props ? props.bundle : props.product
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const {
    recommendationUIData: { [RECOMMENDED_SECTION_IDS.SLOT2]: similarProductsRes },
    updateRecommendationAPIData,
    isRecommendationLoading,
    setHasMoreToExplore,
  } = useProductPageContext()
  const selectedOptions = useAppSelector<ProductVariantOption[]>(pdpStateManager, pdpStateManager.getSelectedOptions)
  const productId = useAppSelector<string | undefined>(pdpStateManager, pdpStateManager.getProductIdFromRedux)

  const { similarProductExpData, newPDPRecommendations } = useProductPageContext()

  const {
    title,
    titleClassname = '',
    showInLineMiniProduct,
    productCardExpClassName = '',
    similarProductsWrapperClassname = '',
    chunkSize = 4,
    infiniteScroll = false,
    showDots,
    wishlistIconOverrideClass,
    swiperClassName,
    hideWishlistIcon,
    hideProductName,
    gridColCount,
    isVertical,
    productPriceClass,
    productCardLinkClass = '',
    hideTags,
    productPriceWrapperClass,
  } = props.isMiniSimilarProduct ? (similarProductExpData.rightSimilarConfig ?? {}) : similarProductExpData

  const shouldShowNewSimilarProductsSection = useIsNewRecommendationsDesign()
  const isNewMTEMobile = shouldShowNewSimilarProductsSection && !isDesktop

  const { fetchData: fetchSimilarProducts, isLoading: isSimilarLoading } = useFetch<IProductCard[], any>(
    getRecommendedProductsApi,
    {
      onlyReturnLatestFetch: true,
    },
  )

  const displayTitle = useMemo(() => similarProductsRes.module_title, [similarProductsRes])

  const getIsVisibleTrackParams = useCallback(() => {
    const productId = product.id
    const selectedColor = selectedOptions.filter(o => o.name == ProductOptionName.Color)[0]?.value
    return {
      // @i18n-ignore
      action: 'pdp_similar_product',
      value: true,
      context: {
        productId,
        selectedColor,
        ...getTrackingMetaData(displayTitle).context,
      },
    }
  }, [product.id, selectedOptions.filter(o => o.name == ProductOptionName.Color)[0]?.value])

  const getRecommendationVisibleTrackParams = useCallback(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.RECOMMENDATION_VIEW, similarProductsRes)
  }, [product.id, similarProductsRes])

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(
    getIsVisibleTrackParams,
    getRecommendationVisibleTrackParams,
  )

  const selectedColor = getSelectedColor(selectedOptions)

  useEffect(() => {
    if (!selectedColor || productId !== product.id.toString() || !newPDPRecommendations.isInitialized) {
      return
    }

    const recommendedProductApiReqParams: IGetRecommendedProductsApiParams = {
      product,
      isNewPDPRecommendations: newPDPRecommendations.isInBucket,
      selectedOptions,
      recommendedProductsType: RECOMMENDED_PRODUCTS_TYPE.MTE,
    }

    fetchSimilarProducts(recommendedProductApiReqParams)
      .then(res => {
        if (res) {
          updateRecommendationAPIData(RECOMMENDED_SECTION_IDS.SLOT2, {
            ...productContextInitialState.recommendationUIData[RECOMMENDED_SECTION_IDS.SLOT2],
            data: res,
          })
        }
      })
      .catch((err: unknown) => {
        captureException(err, {
          module: MODULES.PRODUCT,
          feature: FEATURES.MTE,
        })
      })
  }, [selectedColor, productId, newPDPRecommendations.isInitialized])

  const hideSimilarProducts = !isRecommendationLoading && !isSimilarLoading && similarProductsRes.data.length === 0

  const carouselArrowClickTracker = ({ direction }: { direction: string }) => {
    const clickDirectionEvent =
      direction === 'prev' ? TELEMETRY_EVENT_NAMES.CAROUSEL_LEFT_CLICK : TELEMETRY_EVENT_NAMES.CAROUSEL_RIGHT_CLICK
    trackEvent(clickDirectionEvent, {
      module_name: similarProductsRes.module_name,
      module_id: similarProductsRes.module_id,
    })
  }

  const additionalProductTrackingContext = () => {
    return {
      source_product_id: product.shopifyProductId,
      source_product_color: getSelectedColorFromSelectedOptions(selectedOptions),
      isRecommendationProduct: true,
    }
  }

  useEffect(() => {
    setHasMoreToExplore(!hideSimilarProducts)
  }, [hideSimilarProducts])

  return (
    <div
      id={props.mteId}
      ref={trackIsVisibleRef}
      style={{ display: hideSimilarProducts ? 'none' : undefined }}
      className={classNames(
        styles.similarProducts,
        isNewMTEMobile && styles.similarProducts__newDesign,
        similarProductsWrapperClassname,
        {
          [styles.miniSimilarProducts ?? '']: props.isMiniSimilarProduct,
        },
      )}
      data-section-name={SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF}
    >
      {!isRecommendationLoading && !isSimilarLoading ? (
        <h2 className={classNames(styles.similarProducts__title, titleClassname)}>
          {title ?? t(similarProductsRes.module_title_key)}
        </h2>
      ) : null}

      <LoaderWrapper
        showLoader={isRecommendationLoading || isSimilarLoading}
        minHeight={showInLineMiniProduct ? '9rem' : undefined}
      >
        <>
          {isSimilarLoading ? null : props.isMiniSimilarProduct ? (
            <MiniSimilarProducts productCards={[]} />
          ) : (
            <ProductListSectionTwo
              productCards={similarProductsRes.data}
              wrapperClassName={styles.productListSection}
              productCardDesktopClassName={styles.productListSection__desktopProductCard}
              productCardMobileClassName={styles.productListSection__mobileProductCard}
              productCardWrapperMobileClassName={styles.productListSection__productCardWrapperMobile}
              hideProductOptions={true}
              hideProductReviews={true}
              navPosition={NAV_POSITION.RIGHT}
              // @i18n-ignore
              tracker={`similar_products`}
              skipTagRefresh={true}
              productClickTrackConfig={{
                trackInternal: true,
                // @i18n-ignore
                category: 'similar_products',
                // @i18n-ignore
                action: 'click_similar_product',
                context: {
                  source_product_id: product.shopifyProductId,
                  source_product_color: getSelectedColorFromSelectedOptions(selectedOptions),
                  ...getTrackingMetaData(displayTitle).context,
                  findingMethod: FINDING_METHOD_AGGREGATE.RECOMMENDATION,
                },
              }}
              trackingMetaData={getTrackingMetaData(displayTitle)}
              numberOfSlidesToScroll={isDesktop ? 4 : 1}
              // numberOfVisibleSlides={1}
              showInLineMiniProduct={showInLineMiniProduct}
              productCardExpClassName={productCardExpClassName}
              chunkSize={shouldShowNewSimilarProductsSection ? 9 : chunkSize}
              isNewDesign={shouldShowNewSimilarProductsSection}
              infiniteScroll={infiniteScroll}
              showDots={isNewMTEMobile ? true : showDots}
              wishlistIconOverrideClass={wishlistIconOverrideClass}
              swiperClassName={swiperClassName}
              hideWishlistIcon={isNewMTEMobile ? true : hideWishlistIcon}
              hideProductName={hideProductName}
              gridColCount={gridColCount}
              isVertical={isVertical}
              productPriceClass={isNewMTEMobile ? styles.productPriceClassNewDesign : productPriceClass}
              productCardLinkClass={isNewMTEMobile ? styles.productCardLink : productCardLinkClass}
              productPriceWrapperClass={productPriceWrapperClass}
              additionalProductTrackingContext={additionalProductTrackingContext}
              carouselArrowClickTracker={carouselArrowClickTracker}
              hideTags={hideTags}
              productCardImageSectionClass={isNewMTEMobile ? styles.productCardImageSectionClass : undefined}
            />
          )}
        </>
      </LoaderWrapper>
    </div>
  )
}

export default SimilarProducts
